import React from 'react';
import {
    IndexTable,
    IndexFilters,
    useSetIndexFiltersMode,
    useBreakpoints,
    Button,
    ButtonGroup,
    Badge,
    Tooltip,
    Card,
} from '@shopify/polaris';
import { EditIcon } from '@shopify/polaris-icons';
import { dateFormat, dynamicResourceName, limit, RULE_STATUS } from '../../utils';

const RuleTable = ({
    rules,
    totalRulesCount,
    onPaginationChange,
    isRuleLoading,
    handleFiltersQueryChange,
    handleQueryValueRemove,
    searchValue,
    selected,
    setSelected,
    sortSelected,
    setSortSelected,
    page,
    setPage,
    handleEdit,
    handleDelete,
    selectedResources,
    allResourcesSelected,
    handleSelectionChange
}) => {
    const tabs = RULE_STATUS.map((item, index) => ({
        content: item.label,
        index,
        onAction: () => {
            setSelected(item.selected);
        },
        id: `${item.value}-${index}`,
        isLocked: index === 0,
    }));
    const sortOptions = [
        { label: 'Created At', value: 'createdAt asc', directionLabel: 'Ascending' },
        { label: 'Created At', value: 'createdAt desc', directionLabel: 'Descending' }
    ];
    const { mode, setMode } = useSetIndexFiltersMode();

    const promotedBulkActions = [
        {
            destructive: true,
            content: selectedResources.length > 1 ? 'Delete rules' : 'Delete rule',
            onAction: (e) => handleDelete(e)
        },
    ];

    const rowMarkup =  rules.length > 0 ? rules.map(
        (
            { _id: id, name, status, scope, settings, createdAt, updatedAt },
            index,  
        ) => {
            return (
                <IndexTable.Row
                    id={id}
                    key={id}
                    selected={selectedResources.includes(id)}
                    position={index}
                >
                    <IndexTable.Cell>
                        {name?.length > 20 ?
                            <Tooltip content={name}>
                                {name.slice(0, 20)}...
                            </Tooltip>
                        : name}
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                        {scope[0]?.variantIDs.length || '0'} variants
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                        <Badge tone={status === 'active' ? 'success' : 'critical'}>
                            {status}
                        </Badge>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                        {settings.auto_apply ? <Badge tone='success'>Yes</Badge> : <Badge tone='attention'>No</Badge>}
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                        {createdAt ? dateFormat(createdAt) : 'N/A'}
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                        {updatedAt ? dateFormat(updatedAt) : 'N/A'}
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                        <ButtonGroup>
                            {/* <Tooltip content='View rule'>
                                <Button icon={ViewIcon} variant='secondary' tone='success'></Button>
                            </Tooltip> */}
                            <Tooltip content='Edit rule'>
                                <Button onClick={(e) => handleEdit(e, id)} icon={EditIcon} variant='secondary' tone='success'></Button>
                            </Tooltip>
                        </ButtonGroup>
                    </IndexTable.Cell>
                </IndexTable.Row>
            );
        }
    ) : null;

    const handlePageNext = () => {
        onPaginationChange({
            page: page + 1,
            status: selected,
            search: searchValue,
            limit: limit
        });
        setPage(page + 1);
    };

    const handlePagePrevious = () => {
        onPaginationChange({
            page: page - 1,
            status: selected,
            search: searchValue,
            limit: limit
        });
        setPage(page - 1);
    };

    return (
        <Card padding='0' roundedAbove='sm'>
            <IndexFilters
                sortOptions={sortOptions}
                sortSelected={sortSelected}
                queryValue={searchValue}
                queryPlaceholder="Searching in all"
                onQueryChange={handleFiltersQueryChange}
                onQueryClear={handleQueryValueRemove}
                onSort={setSortSelected}
                cancelAction={{
                    onAction: handleQueryValueRemove,
                    disabled: false,
                    loading: false,
                }}
                tabs={tabs}
                selected={selected}
                onSelect={setSelected}
                canCreateNewView={false}
                onCreateNewView={() => { }}
                filters={[]}
                appliedFilters={[]}
                onClearAll={handleQueryValueRemove}
                mode={mode}
                setMode={setMode}
            />
            <IndexTable
                condensed={useBreakpoints().smDown}
                resourceName={dynamicResourceName('rule')}
                itemCount={rules.length}
                selectedItemsCount={
                    allResourcesSelected ? 'All' : selectedResources.length
                }
                onSelectionChange={handleSelectionChange}
                selectable={true}
                loading={isRuleLoading}
                promotedBulkActions={promotedBulkActions}
                headings={[
                    { title: 'Name' },
                    { title: 'Variant Count' },
                    { title: 'Status' },
                    { title: 'Auto Apply' },
                    { title: 'Created At' },
                    { title: 'Updated At' },
                    { title: 'Action' }
                ]}
                pagination={{
                    hasNext: page < totalRulesCount / limit,
                    onNext: handlePageNext,
                    label: `${(page - 1) * limit + 1}–${Math.min(
                        page * limit,
                        totalRulesCount
                    )} of ${totalRulesCount} rules`,
                    hasPrevious: page > 1,
                    onPrevious: handlePagePrevious,
                }}
            >
                {rowMarkup}
            </IndexTable>
        </Card>
    );
};

export default RuleTable;